'use client'

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
  useRef
} from 'react'
import type { GameweekNavigationRow } from '@/lib/utils/gameweekNavigation'
import { useNavigationData } from './NavigationProvider'
import {
  resolveAccessibleDashboardGameweeks,
  resolveTrackedSelectedGameweek
} from '@/lib/utils/gameweekNavigation'

interface GameweekData extends GameweekNavigationRow {
  id: string
  gameweek_number: number
  start_date: string | null
  end_date: string | null
  deadline: string
  status: 'past' | 'current' | 'future'
}

interface GameweekNavigationContextData {
  selectedGameweek: number
  availableGameweeks: GameweekData[]
  currentGameweek: number
  canNavigatePrevious: boolean
  canNavigateNext: boolean
  isLoading: boolean
  error: string | null
  navigateToPrevious: () => void
  navigateToNext: () => void
  navigateToGameweek: (gameweek: number) => void
}

const GameweekNavigationContext = createContext<GameweekNavigationContextData>({
  selectedGameweek: 0, // 0 = uninitialized, waiting for real data
  availableGameweeks: [],
  currentGameweek: 0, // 0 = no real data yet
  canNavigatePrevious: false,
  canNavigateNext: false,
  isLoading: false,
  error: null,
  navigateToPrevious: () => {},
  navigateToNext: () => {},
  navigateToGameweek: () => {}
})

interface GameweekNavigationProviderProps {
  children: ReactNode
}

export function GameweekNavigationProvider({ children }: GameweekNavigationProviderProps) {
  const { availableGameweeks: navigationGameweeks, isLoading, error } = useNavigationData()
  const availableGameweeks = React.useMemo(
    () =>
      navigationGameweeks.map((row) => ({
        ...row,
        effective_deadline: row.effectiveDeadline,
        effective_state: row.effectiveState,
        editing_allowed: row.editingAllowed
      })),
    [navigationGameweeks]
  )
  const [selectedGameweek, setSelectedGameweek] = useState<number>(0) // Use 0 as uninitialized state
  const autoTrackedGameweekRef = useRef<number | null>(0)

  const [clock, setClock] = useState(() => Date.now())
  const accessibleGameweeks = resolveAccessibleDashboardGameweeks(
    availableGameweeks,
    new Date(clock)
  )
  const currentGameweek = accessibleGameweeks.at(-1)?.gameweek_number ?? 0
  // Never expose a removed or newly locked selection, even for a single render.
  const safeSelectedGameweek = accessibleGameweeks.some(
    (row) => row.gameweek_number === selectedGameweek
  )
    ? selectedGameweek
    : currentGameweek

  useEffect(() => {
    const refresh = () => setClock(Date.now())
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [])

  useEffect(() => {
    const refresh = setTimeout(() => setClock(Date.now()), 0)
    return () => clearTimeout(refresh)
  }, [availableGameweeks])

  // Advance an open dashboard at the deadline without introducing network polling.
  useEffect(() => {
    const now = Date.now()
    const nextDeadline = Math.min(
      ...availableGameweeks
        .map((row) => Date.parse(row.effective_deadline ?? row.deadline))
        .filter((time) => time > now)
    )
    if (!Number.isFinite(nextDeadline)) return
    const timer = setTimeout(() => setClock(Date.now()), Math.min(nextDeadline - now, 2147483647))
    return () => clearTimeout(timer)
  }, [availableGameweeks, clock])

  // Keep the selected gameweek aligned with the computed current gameweek until the user
  // intentionally browses to a historical week.
  useEffect(() => {
    if (!currentGameweek) return

    setSelectedGameweek((previousSelectedGameweek) => {
      const nextSelectedGameweek = resolveTrackedSelectedGameweek({
        autoTrackedGameweek: autoTrackedGameweekRef.current,
        currentGameweek,
        selectedGameweek: previousSelectedGameweek
      })

      if (nextSelectedGameweek === currentGameweek) {
        autoTrackedGameweekRef.current = currentGameweek
      }

      return nextSelectedGameweek
    })
  }, [currentGameweek, selectedGameweek])

  // Recheck the real clock at interaction time, including after suspended tabs.
  const navigateToGameweek = useCallback(
    (gameweek: number) => {
      const now = Date.now()
      const allowed = resolveAccessibleDashboardGameweeks(availableGameweeks, new Date(now))
      if (!allowed.some((row) => row.gameweek_number === gameweek)) return
      const current = allowed.at(-1)?.gameweek_number ?? 0
      autoTrackedGameweekRef.current = gameweek === current ? current : null
      setClock(now)
      setSelectedGameweek(gameweek)
    },
    [availableGameweeks]
  )

  const navigateToPrevious = () => {
    const previous = resolveAccessibleDashboardGameweeks(availableGameweeks)
      .filter((row) => row.gameweek_number < safeSelectedGameweek)
      .at(-1)
    if (previous) navigateToGameweek(previous.gameweek_number)
  }

  const navigateToNext = () => {
    const next = resolveAccessibleDashboardGameweeks(availableGameweeks).find(
      (row) => row.gameweek_number > safeSelectedGameweek
    )
    if (next) navigateToGameweek(next.gameweek_number)
  }

  const contextValue: GameweekNavigationContextData = {
    selectedGameweek: safeSelectedGameweek,
    availableGameweeks,
    currentGameweek: currentGameweek || 0, // Show 0 when no real data, never fake data
    canNavigatePrevious: accessibleGameweeks.some(
      (row) => row.gameweek_number < safeSelectedGameweek
    ),
    canNavigateNext: accessibleGameweeks.some((row) => row.gameweek_number > safeSelectedGameweek),
    isLoading,
    error,
    navigateToPrevious,
    navigateToNext,
    navigateToGameweek
  }

  return (
    <GameweekNavigationContext.Provider value={contextValue}>
      {children}
    </GameweekNavigationContext.Provider>
  )
}

export function useGameweekNavigation() {
  const context = useContext(GameweekNavigationContext)
  if (!context) {
    throw new Error('useGameweekNavigation must be used within a GameweekNavigationProvider')
  }
  return context
}
